import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import { WO2Checkbox, WO2Select } from 'src/common';
import DateRangePicker from 'src/common/components/DateRangePicker';
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BaseReportComponentProps, GroupByPortfolioValuation, MdaReportType, MdaReportTypeField, MdaReportTypes } from '../../store/types';
import { ExpandMore } from '@mui/icons-material';
import { DateRange } from 'src/store';
import { ClientPortfolio } from '../../../common/store';
import { ReportGroupingOption } from '../ReportGroupingOption';

export interface PortfolioValuationCondensedProps extends BaseReportComponentProps {
  clientPortfolios: ClientPortfolio[];
  expandPortfolioValuationCondensed: ActionCreatorWithoutPayload<string>;
  setPortfolioValuationCondensedSelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setPortfolioValuationCondensedGroupByParameter: ActionCreatorWithPayload<GroupByPortfolioValuation, string>;
  setAsAtDatePortfolioValuationCondensedParameter: ActionCreatorWithPayload<DateRange, string>;
  setPortfolioValuationCondensedPortfolioIdParameter?: ActionCreatorWithPayload<number, string>;
}

export const PortfolioValuationCondensed = ({
  parameters,
  createReportSortRequest,
  setPortfolioValuationCondensedSelectedParameter,
  setPortfolioValuationCondensedGroupByParameter,
  setAsAtDatePortfolioValuationCondensedParameter,
  setPortfolioValuationCondensedPortfolioIdParameter,
  expandPortfolioValuationCondensed,
  clientPortfolios,
  setGroupConsolidatedOption,
  disablePortfolioSelect,
}: PortfolioValuationCondensedProps): JSX.Element => {
  const report = parameters.selectedReports.portfolioValuationCondensed;

  const handleClickSelect = () => {
    setPortfolioValuationCondensedSelectedParameter(!report.selected);
    createReportSortRequest(!report.selected, MdaReportTypes.PortfolioValuationCondensed.id, MdaReportTypes.PortfolioValuationCondensed.displayName);
  };

  const handleSelectPortfolio = (portfolio: ClientPortfolio | null) => {
    if (!setPortfolioValuationCondensedPortfolioIdParameter) return;
    setPortfolioValuationCondensedPortfolioIdParameter(portfolio?.investmentProgramId ?? -1);
  };

  return (
    <Accordion onChange={expandPortfolioValuationCondensed} expanded={report.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          aria-label="Acknowledge"
          sx={{ flex: 1 }}
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={<WO2Checkbox color="primary" checked={report.selected} onChangeHandler={handleClickSelect} />}
          label={MdaReportType.PortfolioValuationCondensed}
        />
        <div onClick={(e) => e.stopPropagation()}>
          <DateRangePicker
            sx={{ minWidth: 194 }}
            dateRange={{
              dateFrom: '',
              dateTo: report.asAtDate,
              dateRangeType: report.asAtDateType,
            }}
            dateOnlyLabel="As at"
            setDateRange={setAsAtDatePortfolioValuationCondensedParameter}
            isDateOnly
          />
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container direction="column" spacing={1}>
          <Grid item mb={1}>
            <Typography variant="body2">
              Includes: Code, Name, Quantity · Avg Unit Cost, Cost Base · Price, Value, Port % · Est Gain/Loss, Gain/Loss % · Est Income, Yield.
            </Typography>
            <Typography variant="body2">To choose your own columns, use {MdaReportType.PortfolioValuation}.</Typography>
          </Grid>
          <Grid item>
            <FormControl fullWidth>
              <InputLabel id="portfolio-valuation-condensed-group-by-id">Group by</InputLabel>
              <WO2Select
                labelId="portfolio-valuation-condensed-group-by-id"
                label="group by"
                onChange={(e) => setPortfolioValuationCondensedGroupByParameter(e.target.value as GroupByPortfolioValuation)}
                value={report.groupBy}
                defaultValue={GroupByPortfolioValuation.AssetClass}
              >
                <MenuItem value={GroupByPortfolioValuation.AssetClass}>Asset Class</MenuItem>
                <MenuItem value={GroupByPortfolioValuation.Gics}>GICS</MenuItem>
                <MenuItem value={GroupByPortfolioValuation.Portfolio}>Portfolio</MenuItem>
                <MenuItem value={GroupByPortfolioValuation.Model}>Model</MenuItem>
              </WO2Select>
            </FormControl>
          </Grid>
          {setPortfolioValuationCondensedPortfolioIdParameter && !disablePortfolioSelect && (
            <Grid item>
              <Autocomplete
                noOptionsText="No items to select"
                id="portfolio-valuation-condensed-investmentservice-multiselect"
                fullWidth
                value={clientPortfolios.find((portfolio) => portfolio.investmentProgramId === report.portfolioId) || null}
                options={clientPortfolios}
                getOptionLabel={(option: ClientPortfolio) => option.investmentServiceName}
                renderInput={(params) => <TextField {...params} label="Select a Portfolio" margin="normal" variant="outlined" fullWidth />}
                onChange={(_e: React.SyntheticEvent<Element, Event>, value: ClientPortfolio | null) => handleSelectPortfolio(value)}
                isOptionEqualToValue={(option: ClientPortfolio, value: ClientPortfolio) => option.investmentProgramId === value.investmentProgramId}
              />
            </Grid>
          )}
          {setGroupConsolidatedOption && (
            <Grid item container spacing={4} direction="column">
              <Grid item>
                <Divider />
              </Grid>
              <Grid item>
                <ReportGroupingOption
                  value={report.groupOption}
                  setGroupConsolidatedOption={setGroupConsolidatedOption}
                  report={MdaReportTypeField.PortfolioValuationCondensed}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
